import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Sparkles, Upload, Image as ImageIcon, Download,
  Wand2, Camera, Hash, Crown, RefreshCw, Copy, Check, Layers, Palette
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { runToolJob } from "@/lib/toolJobs";
import { useBrandDnaSummary } from "@/lib/useBrandDna";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Retry axios call on Cloudflare/edge errors — same transient list as toolJobs.
const TRANSIENT_STATUS = new Set([502, 503, 504, 520, 521, 522, 523, 524, 408]);
async function _postWithRetry(url, data, config, { attempts = 3, baseDelayMs = 2000 } = {}) {
  let lastErr;
  for (let i = 1; i <= attempts; i++) {
    try {
      return await axios.post(url, data, config);
    } catch (err) {
      lastErr = err;
      const isTransient = !err.response || TRANSIENT_STATUS.has(err.response.status);
      if (!isTransient || i === attempts) throw err;
      await new Promise(r => setTimeout(r, baseDelayMs * i));
    }
  }
  throw lastErr;
}

const STYLES = [
  { id: "edutainment",   label: "Edutainment", desc: "Überraschende Behauptung, Story, Insight, Tipp, CTA" },
  { id: "story",         label: "Storytelling", desc: "„Stell dir vor...\" Vorher, Krise, Wendepunkt, Nachher, CTA" },
  { id: "pain_solution", label: "Pain zu Lösung", desc: "Brutale Hook, Pain vertiefen, Lösung in Schritten, CTA" },
  { id: "myth_truth",    label: "Mythos vs. Wahrheit", desc: "Provokante Frage, Mythen kontern, Insight, CTA" },
  { id: "tips_list",     label: "Liste / Tipps", desc: "„X Fehler die dich…\" konkrete Tipps, CTA" },
];

const TEMPLATES = [
  { id: "editorial",      label: "Editorial Magazine",  emoji: "📖", font: "Outfit Black",
    desc: "Vogue-Style, Magazin-Look, sharp Typo. Premium & polished." },
  { id: "minimal",        label: "Modern Minimal",      emoji: "◯",  font: "Inter Light",
    desc: "Skandinavisch, viel Weißraum, leise Eleganz. Apple-Keynote-Vibe." },
  { id: "bold_brutalist", label: "Bold & Brutalist",    emoji: "▣",  font: "Druk Wide",
    desc: "Riesige Type, asymmetrisch, kompromisslos. Off-White-Aesthetic." },
  { id: "klassik_serif",  label: "Klassik · Serif",     emoji: "❦",  font: "Playfair Display",
    desc: "Zeitloses Editorial, Bodoni-Serif, Vogue-Archive. Elegant & raffiniert." },
  { id: "abstract",       label: "Abstrakt & Künstlerisch", emoji: "✦", font: "Helvetica + Italic",
    desc: "Bauhaus meets Collage. Geometrische Formen, Gallery-Zine." },
  { id: "soft_romantic",  label: "Soft & Romantic",     emoji: "❀",  font: "Cormorant Light",
    desc: "Warm, dreamy, feminine Serif. Goldene Stunde, breathy Eleganz." },
  { id: "vibrant_pop",    label: "Vibrant Modern Pop",  emoji: "✺",  font: "Outfit + Script",
    desc: "Y2K, energetisch. Gradients & Sparkles. Gen-Z Social." },
  { id: "custom",         label: "Eigenes Design",      emoji: "✍",  font: "Frei wählbar",
    desc: "Beschreibe dein Design selbst — Stil, Schrift, Stimmung. Volle Kontrolle.", isCustom: true },
];

const CUSTOM_PLACEHOLDER = `z.B.:
Großes Foto oben, unten ein heller cremefarbener Block mit einer eleganten Serif-Schrift wie "Cormorant" für die Headline. Stil zeitlos und ruhig, viel Weißraum. Stimmung: warm, vertrauenserweckend, wie ein hochwertiger Coaching-Brand.

Oder:
Brutalistisches Layout, riesige fette Schrift "Druk Wide" die fast das ganze Bild überlagert. Hintergrund körnig, Foto leicht überbelichtet. Stimmung: laut, selbstbewusst, Magazin-Cover.`;

const SLIDE_OPTIONS = [3, 5, 6, 8, 10];

const COLOR_PRESETS = [
  { name: "Lime · Schwarz", accent: "#C8FF4D", bg: "#0a0a0f", text: "#FFFFFF" },
  { name: "Cyan · Schwarz", accent: "#00D4FF", bg: "#0a0a0f", text: "#FFFFFF" },
  { name: "Pink · Schwarz", accent: "#EC4899", bg: "#0a0a0f", text: "#FFFFFF" },
  { name: "Beige · Creme",  accent: "#A78368", bg: "#F5EFE6", text: "#1A1410" },
  { name: "Gold · Marine",  accent: "#D4AF37", bg: "#0F1729", text: "#FFFFFF" },
  { name: "Coral · Stein",  accent: "#FF6F61", bg: "#1F1B17", text: "#F5EFE6" },
];

export default function CarouselPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { summary: dna } = useBrandDnaSummary();

  const [meta, setMeta] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Brief (deep questions)
  const [theme, setTheme] = useState("");
  const [audience, setAudience] = useState("");
  const [ahaMoment, setAhaMoment] = useState("");
  const [desiredAction, setDesiredAction] = useState("");
  const [examples, setExamples] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [style, setStyle] = useState("edutainment");
  const [template, setTemplate] = useState("editorial");
  const [customDescription, setCustomDescription] = useState("");
  const [styleRefUrl, setStyleRefUrl] = useState("");
  const [uploadingStyleRef, setUploadingStyleRef] = useState(false);
  const styleRefFileRef = useRef(null);
  const [useBrandDna, setUseBrandDna] = useState(false);
  const [slides, setSlides] = useState(6);
  const [fmt, setFmt] = useState("1:1");
  const [handle, setHandle] = useState("");
  const [useReference, setUseReference] = useState(true);

  // Branding
  const [accentColor, setAccentColor] = useState("#C8FF4D");
  const [bgColor, setBgColor] = useState("#0a0a0f");
  const [textColor, setTextColor] = useState("#FFFFFF");
  const [showCounter, setShowCounter] = useState(true);
  const [showBrandPanel, setShowBrandPanel] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/carousel/me`);
        if (!cancelled) setMeta(data);
      } catch (e) {
        if (!cancelled) setMeta({ reference_photo_url: "", unlimited: true });
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const jobId = searchParams.get("jobId");
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      try {
        setGenerating(true);
        const startTs = Date.now();
        const maxMs = 6 * 60 * 1000;
        while (!cancelled && Date.now() - startTs < maxMs) {
          const { data: job } = await axios.get(`${API}/tools/jobs/${jobId}`);
          if (cancelled) return;
          if (job.input?.theme) setTheme(job.input.theme);
          if (job.input?.style) setStyle(job.input.style);
          if (job.input?.template) setTemplate(job.input.template);
          if (job.input?.custom_description) setCustomDescription(job.input.custom_description);
          if (job.input?.style_reference_url) setStyleRefUrl(job.input.style_reference_url);
          if (job.input?.use_brand_dna) setUseBrandDna(!!job.input.use_brand_dna);
          if (job.input?.slide_count) setSlides(job.input.slide_count);
          if (job.input?.fmt) setFmt(job.input.fmt);
          if (job.status === "completed" && job.result) { setResult(job.result); return; }
          if (job.status === "failed") { toast.error(job.error || "Fehlgeschlagen"); return; }
          setElapsed(Math.floor((Date.now() - startTs) / 1000));
          await new Promise(r => setTimeout(r, 4000));
        }
      } catch { toast.error("Karussell konnte nicht geladen werden"); }
      finally { if (!cancelled) setGenerating(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) { toast.error("Datei zu groß (max 12 MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await _postWithRetry(`${API}/carousel/upload-reference`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setMeta(prev => ({ ...prev, reference_photo_url: data.reference_photo_url }));
      toast.success("Selfie gespeichert!");
    } catch (e) {
      const msg = !e.response
        ? "Verbindung unterbrochen. Bitte erneut versuchen."
        : (e.response?.data?.detail || `Upload fehlgeschlagen (${e.response?.status})`);
      toast.error(msg);
    } finally { setUploading(false); }
  };

  const handleStyleRefUpload = async (file) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) { toast.error("Datei zu groß (max 12 MB)"); return; }
    setUploadingStyleRef(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await _postWithRetry(`${API}/carousel/upload-style-reference`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setStyleRefUrl(data.style_reference_url);
      toast.success("Vorlage hochgeladen!");
    } catch (e) {
      const msg = !e.response
        ? "Verbindung unterbrochen. Bitte erneut versuchen."
        : (e.response?.data?.detail || `Upload fehlgeschlagen (${e.response?.status})`);
      toast.error(msg);
    } finally { setUploadingStyleRef(false); }
  };

  const applyPreset = (p) => {
    setAccentColor(p.accent);
    setBgColor(p.bg);
    setTextColor(p.text);
  };

  const generate = async () => {
    if (useReference && !meta?.reference_photo_url) {
      toast.error("Bitte zuerst dein Selfie hochladen oder 'ohne Referenzbild' wählen"); return;
    }
    if (!theme.trim()) { toast.error("Bitte ein Thema eingeben"); return; }
    if (template === "custom" && !customDescription.trim() && !styleRefUrl) {
      toast.error("Beschreibe dein Design oder lade eine Vorlage als Style-Referenz hoch."); return;
    }
    setGenerating(true);
    setResult(null);
    setElapsed(0);
    try {
      const data = await runToolJob("post", "/carousel/generate", {
        theme: theme.trim(),
        audience: audience.trim(),
        aha_moment: ahaMoment.trim(),
        desired_action: desiredAction.trim(),
        examples: examples.trim(),
        style, slide_count: slides, fmt,
        platform: "instagram",
        brand_handle: handle.trim().replace(/^@/, ""),
        use_reference: useReference,
        accent_color: accentColor,
        bg_color: bgColor,
        text_color: textColor,
        show_counter: showCounter,
        template,
        custom_description: customDescription.trim(),
        style_reference_url: styleRefUrl,
        use_brand_dna: useBrandDna,
      }, {
        etaSec: 90,
        onTick: ({ elapsedSec }) => setElapsed(elapsedSec),
      });
      setResult(data);
      toast.success("Karussell fertig!");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Generierung fehlgeschlagen");
    } finally { setGenerating(false); }
  };

  const downloadAll = () => {
    if (!result?.slides?.length) return;
    result.slides.forEach((s, i) => {
      const a = document.createElement("a");
      a.href = `data:image/png;base64,${s.png_b64}`;
      a.download = `carousel-slide-${String(i + 1).padStart(2, "0")}.png`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    });
  };

  const copyCaption = async () => {
    const tags = (result?.hashtags || []).map(h => `#${h.replace(/^#/, "")}`).join(" ");
    const text = `${result?.caption || ""}\n\n${tags}`;
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Kopieren fehlgeschlagen"); }
  };

  if (loadingMeta) {
    return <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="carousel-page">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="h-8 w-8 text-zinc-400 hover:text-white" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Layers className="w-5 h-5" style={{ color: '#00D4FF' }} />
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Karussell-Generator</h1>
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-bold" style={{ background: 'rgba(200,255,77,0.12)', color: '#C8FF4D', border: '1px solid rgba(200,255,77,0.3)' }}>NEU</span>
        </div>

        <div className="mb-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs" style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.25)', color: '#7DD8F4' }}>
          <Crown className="w-3 h-3" /> Launch-Phase: Unlimitierte Karussells für alle Premium-Mitglieder
        </div>

        {!result && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* LEFT: Reference Photo & Brand */}
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><Camera className="w-4 h-4" style={{ color: '#00D4FF' }} />Bildquelle</p>
                <div className="flex gap-1 mb-3">
                  <button onClick={() => setUseReference(true)} className={`flex-1 py-2 px-3 rounded-md text-xs font-medium transition-colors ${useReference ? 'text-black' : 'bg-zinc-900 text-zinc-400 border border-zinc-800'}`} style={useReference ? { background: '#00D4FF' } : {}} data-testid="ref-mode-on">Mit Referenzbild (du)</button>
                  <button onClick={() => setUseReference(false)} className={`flex-1 py-2 px-3 rounded-md text-xs font-medium transition-colors ${!useReference ? 'text-black' : 'bg-zinc-900 text-zinc-400 border border-zinc-800'}`} style={!useReference ? { background: '#00D4FF' } : {}} data-testid="ref-mode-off">Ohne (KI-Personen)</button>
                </div>
                <p className="text-xs text-zinc-400 mb-3">{useReference ? "Lade EIN klares Foto deines Gesichts hoch — Frontansicht, gut beleuchtet." : "Die KI generiert für jeden Slide eine fiktive Person passend zur Szene."}</p>
              </div>

              {useReference && (meta?.reference_photo_url ? (
                <div className="relative rounded-xl overflow-hidden border border-cyan-500/30" data-testid="reference-photo-preview">
                  <img src={meta.reference_photo_url} alt="Referenz" className="w-full aspect-square object-cover" />
                  <button onClick={() => fileRef.current?.click()} className="absolute top-2 right-2 px-3 py-1.5 rounded-lg bg-zinc-900/80 backdrop-blur text-xs text-white border border-zinc-700 flex items-center gap-1.5" data-testid="change-reference-btn">
                    <RefreshCw className="w-3 h-3" /> Ändern
                  </button>
                </div>
              ) : (
                <div onClick={() => fileRef.current?.click()} className="cursor-pointer rounded-xl border-2 border-dashed border-zinc-700 hover:border-cyan-500/50 p-8 text-center transition-colors" data-testid="reference-upload-zone">
                  {uploading ? <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" style={{ color: '#00D4FF' }} /> : <Upload className="w-8 h-8 mx-auto mb-2" style={{ color: '#00D4FF' }} />}
                  <p className="text-sm font-medium text-white">{uploading ? "Lädt hoch..." : "Selfie hochladen"}</p>
                  <p className="text-xs text-zinc-500 mt-1">JPG, PNG, WEBP · Max 12 MB</p>
                </div>
              ))}
              {!useReference && (
                <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-6 text-center">
                  <Wand2 className="w-8 h-8 mx-auto mb-2" style={{ color: '#C8FF4D' }} />
                  <p className="text-sm font-medium text-white mb-1">KI-generierte Personen</p>
                  <p className="text-xs text-zinc-400">Die KI erstellt für jeden Slide eine passende Person.</p>
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ""; }} data-testid="reference-file-input" />

              {/* BRAND PANEL */}
              <div className="rounded-xl border border-zinc-800 bg-zinc-900/40">
                <button onClick={() => setShowBrandPanel(s => !s)} className="w-full flex items-center justify-between p-4 text-left" data-testid="toggle-brand-panel">
                  <div className="flex items-center gap-2">
                    <Palette className="w-4 h-4" style={{ color: accentColor }} />
                    <p className="text-sm font-semibold text-white">Dein Branding</p>
                    <span className="text-[10px] text-zinc-500">optional</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-4 h-4 rounded border border-zinc-700" style={{ background: accentColor }} />
                    <span className="w-4 h-4 rounded border border-zinc-700" style={{ background: bgColor }} />
                    <span className="w-4 h-4 rounded border border-zinc-700" style={{ background: textColor }} />
                  </div>
                </button>
                {showBrandPanel && (
                  <div className="px-4 pb-4 space-y-3 border-t border-zinc-800 pt-3" data-testid="brand-panel">
                    <div>
                      <Label className="text-[11px] text-zinc-400 mb-1.5 block">Voreinstellungen</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                        {COLOR_PRESETS.map((p, i) => (
                          <button key={i} onClick={() => applyPreset(p)} className="text-left p-2 rounded-md border border-zinc-800 hover:border-zinc-600 transition-colors" data-testid={`color-preset-${i}`}>
                            <div className="flex gap-0.5 mb-1">
                              <span className="w-3 h-3 rounded-sm" style={{ background: p.accent }} />
                              <span className="w-3 h-3 rounded-sm" style={{ background: p.bg }} />
                              <span className="w-3 h-3 rounded-sm" style={{ background: p.text }} />
                            </div>
                            <span className="text-[10px] text-zinc-300">{p.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <ColorRow label="Akzentfarbe" desc="Highlight-Unterstrich, kleine Akzente" value={accentColor} onChange={setAccentColor} testid="color-accent" />
                    <ColorRow label="Hintergrundfarbe" desc="Untere Text-Fläche der Slides" value={bgColor} onChange={setBgColor} testid="color-bg" />
                    <ColorRow label="Textfarbe" desc="Headline-Schrift" value={textColor} onChange={setTextColor} testid="color-text" />
                    <div className="pt-2 border-t border-zinc-800">
                      <Label className="text-[11px] text-zinc-400 mb-1.5 block">Dein Insta-Handle (optional, wird unten links auf Slides eingefügt)</Label>
                      <Input value={handle} onChange={e => setHandle(e.target.value)} placeholder="@deinhandle" className="bg-zinc-950 border-zinc-800 text-white text-sm h-9" data-testid="handle-input" />
                    </div>
                    <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer pt-1">
                      <input type="checkbox" checked={showCounter} onChange={e => setShowCounter(e.target.checked)} className="accent-cyan-500" data-testid="toggle-counter" />
                      Slide-Counter (z.B. „01/06") anzeigen
                    </label>
                  </div>
                )}
              </div>

              {/* Brand DNA hint moved into the toggle on the right column */}
            </div>

            {/* RIGHT: Brief + Style */}
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-zinc-400 mb-1.5 block">
                  Worüber soll der Beitrag gehen? <span style={{ color: '#C8FF4D' }}>*</span>
                </Label>
                <Textarea value={theme} onChange={(e) => setTheme(e.target.value)} placeholder="z.B. Warum 90% aller Coachinnen unsichtbar bleiben — und was sie stattdessen tun sollten" className="bg-zinc-900 border-zinc-800 text-white text-sm min-h-[70px]" data-testid="theme-input" />
              </div>

              <button onClick={() => setShowAdvanced(s => !s)} className="text-xs flex items-center gap-1.5 transition-colors" style={{ color: showAdvanced ? '#C8FF4D' : '#7DD8F4' }} data-testid="toggle-advanced">
                <Sparkles className="w-3 h-3" />
                {showAdvanced ? "Tiefe Fragen ausblenden" : "Tiefer graben für bessere Ergebnisse"}
              </button>

              {showAdvanced && (
                <div className="space-y-3 p-3 rounded-lg bg-zinc-900/40 border border-zinc-800" data-testid="advanced-brief">
                  <div>
                    <Label className="text-[11px] text-zinc-400 mb-1 block">Wer ist deine Zielgruppe?</Label>
                    <Input value={audience} onChange={e => setAudience(e.target.value)} placeholder="z.B. Coachinnen 30-45, Online-Business-Start" className="bg-zinc-950 border-zinc-800 text-white text-xs h-9" data-testid="audience-input" />
                  </div>
                  <div>
                    <Label className="text-[11px] text-zinc-400 mb-1 block">Dein persönlicher Aha-Moment / Story-Anker</Label>
                    <Textarea value={ahaMoment} onChange={e => setAhaMoment(e.target.value)} placeholder="z.B. Ich hab 3 Jahre Content gepostet ohne Verkäufe, bis ich meine Brand DNA klar hatte — dann nach 6 Wochen erster 5k-Launch." className="bg-zinc-950 border-zinc-800 text-white text-xs min-h-[60px]" data-testid="aha-input" />
                  </div>
                  <div>
                    <Label className="text-[11px] text-zinc-400 mb-1 block">Was soll der Leser am Ende tun?</Label>
                    <Input value={desiredAction} onChange={e => setDesiredAction(e.target.value)} placeholder="z.B. Speichern, mir 'INFO' schreiben, Bio-Link klicken" className="bg-zinc-950 border-zinc-800 text-white text-xs h-9" data-testid="action-input" />
                  </div>
                  <div>
                    <Label className="text-[11px] text-zinc-400 mb-1 block">Konkrete Zahlen / Beispiele (optional)</Label>
                    <Textarea value={examples} onChange={e => setExamples(e.target.value)} placeholder="z.B. 87% der Profile haben keinen klaren Hook · meine Kundin Lisa: 0 → 1.200 Follower in 8 Wochen" className="bg-zinc-950 border-zinc-800 text-white text-xs min-h-[50px]" data-testid="examples-input" />
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs text-zinc-400 mb-2 block">Storytelling-Stil</Label>
                <div className="grid grid-cols-1 gap-1.5" data-testid="style-options">
                  {STYLES.map(s => (
                    <button key={s.id} onClick={() => setStyle(s.id)}
                      className={`text-left p-2.5 rounded-lg border text-xs transition-all ${style === s.id ? 'border-cyan-500 bg-cyan-500/10 text-white' : 'border-zinc-800 hover:border-zinc-700 text-zinc-400'}`}
                      data-testid={`style-${s.id}`}>
                      <p className="font-semibold mb-0.5">{s.label}</p>
                      <p className="text-zinc-500 text-[11px] leading-relaxed">{s.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs text-zinc-400">Visuelles Template</Label>
                  <span className="text-[10px]" style={{ color: '#C8FF4D' }}>NEU</span>
                </div>
                <div className="grid grid-cols-2 gap-1.5" data-testid="template-options">
                  {TEMPLATES.map(t => {
                    const active = template === t.id;
                    return (
                      <button key={t.id} onClick={() => setTemplate(t.id)}
                        className={`relative text-left p-2.5 rounded-lg border text-xs transition-all overflow-hidden ${t.isCustom ? 'col-span-2' : ''} ${active ? 'border-cyan-500 bg-cyan-500/5 text-white' : 'border-zinc-800 hover:border-zinc-700 text-zinc-300'}`}
                        data-testid={`template-${t.id}`}>
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="text-base leading-none">{t.emoji}</span>
                          <p className="font-semibold text-[11px]">{t.label}</p>
                          {t.isCustom && (
                            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold ml-auto"
                                  style={{ background: 'rgba(200,255,77,0.12)', color: '#C8FF4D' }}>
                              FREITEXT
                            </span>
                          )}
                        </div>
                        <p className="text-zinc-500 text-[10px] leading-snug mb-2 line-clamp-2">{t.desc}</p>
                        <span className="inline-block text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-mono"
                              style={{ background: 'rgba(255,255,255,0.06)', color: '#9A9A9F', border: '1px solid rgba(255,255,255,0.08)' }}>
                          {t.font}
                        </span>
                        {active && (
                          <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full" style={{ background: '#C8FF4D' }} />
                        )}
                      </button>
                    );
                  })}
                </div>
                {template === "custom" && (
                  <div className="mt-3 p-3 rounded-lg space-y-3" style={{ background: 'rgba(200,255,77,0.04)', border: '1px solid rgba(200,255,77,0.25)' }} data-testid="custom-template-panel">
                    <div>
                      <Label className="text-[11px] text-zinc-300 mb-1.5 block flex items-center gap-1.5">
                        <span style={{ color: '#C8FF4D' }}>✍</span>
                        Beschreibe dein Design in eigenen Worten
                      </Label>
                      <Textarea
                        value={customDescription}
                        onChange={e => setCustomDescription(e.target.value)}
                        placeholder={CUSTOM_PLACEHOLDER}
                        className="bg-zinc-950 border-zinc-800 text-white text-xs min-h-[140px] leading-relaxed font-mono"
                        data-testid="custom-description-input"
                      />
                      <p className="text-[10px] text-zinc-500 mt-1.5 leading-relaxed">
                        Tipp: Erwähne <span className="text-zinc-300">Stil, Schrift, Komposition, Stimmung</span>. Beispiele:
                        „Outfit Black Headline", „Bauhaus-Formen", „Goldene Stunde Foto", „Magazin-Layout 2-spaltig". Die KI nutzt deine Beschreibung als dominante Stilvorgabe (Farben kommen weiter aus den Pickern oben).
                      </p>
                      <p className="text-[10px] mt-1" style={{ color: customDescription.length > 1200 ? '#FF6F61' : '#9A9A9F' }}>
                        {customDescription.length} / 1200 Zeichen
                      </p>
                    </div>

                    <div className="pt-3 border-t" style={{ borderColor: 'rgba(200,255,77,0.15)' }}>
                      <Label className="text-[11px] text-zinc-300 mb-1.5 block flex items-center gap-1.5">
                        <span style={{ color: '#C8FF4D' }}>🖼</span>
                        Vorlage als Style-Referenz <span className="text-zinc-500 font-normal">(optional)</span>
                      </Label>
                      <p className="text-[10px] text-zinc-500 leading-relaxed mb-2">
                        Hast du auf Canva, Pinterest oder anderswo ein Karussell-Design gefunden, das dir gefällt?
                        Lade einen Screenshot hoch — die KI übernimmt Layout, Komposition & Stimmung. Dein Foto und Text bleiben deine.
                      </p>
                      {styleRefUrl ? (
                        <div className="relative rounded-lg overflow-hidden border border-zinc-700" data-testid="style-reference-preview">
                          <img src={styleRefUrl} alt="Style Referenz" className="w-full max-h-64 object-contain bg-zinc-950" />
                          <div className="absolute top-2 right-2 flex gap-1.5">
                            <button onClick={() => styleRefFileRef.current?.click()}
                                    className="px-2.5 py-1 rounded bg-zinc-900/80 backdrop-blur text-[10px] text-white border border-zinc-700 flex items-center gap-1"
                                    data-testid="change-style-reference-btn">
                              <RefreshCw className="w-3 h-3" /> Ändern
                            </button>
                            <button onClick={() => setStyleRefUrl("")}
                                    className="px-2.5 py-1 rounded bg-zinc-900/80 backdrop-blur text-[10px] text-white border border-zinc-700"
                                    data-testid="remove-style-reference-btn">
                              Entfernen
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div onClick={() => styleRefFileRef.current?.click()}
                             className="cursor-pointer rounded-lg border-2 border-dashed border-zinc-700 hover:border-cyan-500/50 p-4 text-center transition-colors"
                             data-testid="style-reference-upload-zone">
                          {uploadingStyleRef
                            ? <Loader2 className="w-5 h-5 animate-spin mx-auto mb-1.5" style={{ color: '#C8FF4D' }} />
                            : <Upload className="w-5 h-5 mx-auto mb-1.5" style={{ color: '#C8FF4D' }} />}
                          <p className="text-xs font-medium text-white">{uploadingStyleRef ? "Lädt hoch..." : "Vorlage hochladen"}</p>
                          <p className="text-[10px] text-zinc-500 mt-0.5">JPG, PNG, WEBP · Max 12 MB</p>
                        </div>
                      )}
                      <input ref={styleRefFileRef} type="file" accept="image/jpeg,image/png,image/webp"
                             className="hidden"
                             onChange={(e) => { const f = e.target.files?.[0]; if (f) handleStyleRefUpload(f); e.target.value = ""; }}
                             data-testid="style-reference-file-input" />
                    </div>
                  </div>
                )}
              </div>

              {dna && (
                <label className="flex items-start gap-2.5 p-3 rounded-lg cursor-pointer transition-colors"
                       style={{
                         background: useBrandDna ? 'rgba(200,255,77,0.08)' : 'rgba(200,255,77,0.03)',
                         border: `1px solid ${useBrandDna ? 'rgba(200,255,77,0.4)' : 'rgba(200,255,77,0.15)'}`,
                       }}
                       data-testid="brand-dna-toggle">
                  <input type="checkbox" checked={useBrandDna} onChange={e => setUseBrandDna(e.target.checked)}
                         className="accent-lime-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold" style={{ color: '#C8FF4D' }}>Brand DNA mit einbeziehen</p>
                    <p className="text-[11px] text-zinc-400 leading-relaxed mt-0.5">
                      Optional. Nutzt deine gespeicherte Brand DNA ({dna.niche || 'deine Nische'}) für Tonalität und Storyline.
                    </p>
                  </div>
                </label>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400 mb-2 block">Anzahl Slides</Label>
                  <div className="flex flex-wrap gap-1">
                    {SLIDE_OPTIONS.map(n => (
                      <button key={n} onClick={() => setSlides(n)}
                        className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${slides === n ? 'text-black' : 'bg-zinc-900 text-zinc-400 border border-zinc-800'}`}
                        style={slides === n ? { background: '#00D4FF' } : {}}
                        data-testid={`slides-${n}`}>{n}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-zinc-400 mb-2 block">Format</Label>
                  <div className="flex gap-1">
                    {[{id: '1:1', label: 'Quadrat'}, {id: '9:16', label: 'Story'}].map(f => (
                      <button key={f.id} onClick={() => setFmt(f.id)}
                        className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${fmt === f.id ? 'text-black' : 'bg-zinc-900 text-zinc-400 border border-zinc-800'}`}
                        style={fmt === f.id ? { background: '#00D4FF' } : {}}
                        data-testid={`fmt-${f.id.replace(':','-')}`}>{f.label}</button>
                    ))}
                  </div>
                </div>
              </div>

              <Button
                onClick={generate}
                disabled={generating || !theme.trim() || (useReference && !meta?.reference_photo_url)}
                className="w-full text-black font-semibold h-11 transition-all hover:opacity-90"
                style={{ background: `linear-gradient(135deg, #00D4FF 0%, ${accentColor} 100%)` }}
                data-testid="generate-btn"
              >
                {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Erstelle Karussell...</> : <><Wand2 className="w-4 h-4 mr-2" />Karussell generieren</>}
              </Button>
              {generating && (
                <div className="text-xs text-zinc-400 text-center space-y-1">
                  <p>{elapsed > 0 ? `~${elapsed}s gelaufen` : "Starte..."} · KI generiert {slides} Magazin-Slides (typisch 30–90s)</p>
                  {elapsed > 25 && <p className="text-zinc-500">Tab kannst du offen lassen oder später aus „Meine letzten Analysen" wieder öffnen.</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-6" data-testid="carousel-result">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: '#C8FF4D' }}>Fertig</p>
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{result.title}</h2>
                <p className="text-xs text-zinc-500 mt-1">{result.slides?.length || 0} Slides · {result.format} · {STYLES.find(s => s.id === result.style)?.label || result.style}{result.template ? ` · ${TEMPLATES.find(t => t.id === result.template)?.label || result.template}` : ''}</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => { setResult(null); setTheme(""); }} variant="outline" className="border-zinc-700 text-zinc-300" data-testid="new-carousel-btn">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Neues Karussell
                </Button>
                <Button onClick={downloadAll} className="text-black font-semibold" style={{ background: '#00D4FF' }} data-testid="download-all-btn">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Alle Slides ({result.slides?.length})
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="slides-grid">
              {(result.slides || []).map((s, i) => (
                <div key={i} className="group relative rounded-xl overflow-hidden border border-zinc-800 hover:border-cyan-500/40 transition-colors" data-testid={`slide-${i + 1}`}>
                  <img src={`data:image/png;base64,${s.png_b64}`} alt={`Slide ${i + 1}`} className="w-full block" />
                  <a href={`data:image/png;base64,${s.png_b64}`} download={`carousel-slide-${String(i + 1).padStart(2, '0')}.png`}
                     className="absolute bottom-2 right-2 p-2 rounded-md bg-zinc-900/80 backdrop-blur text-white opacity-0 group-hover:opacity-100 transition-opacity"
                     data-testid={`slide-download-${i + 1}`}>
                    <Download className="w-4 h-4" />
                  </a>
                </div>
              ))}
            </div>
            <div className="rounded-xl bg-zinc-900/60 border border-zinc-800 p-4">
              <div className="flex items-start justify-between mb-2 gap-2">
                <div className="flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" style={{ color: '#00D4FF' }} />
                  <p className="text-sm font-semibold text-white">Caption + Hashtags</p>
                </div>
                <button onClick={copyCaption} className="text-xs flex items-center gap-1" style={{ color: '#00D4FF' }} data-testid="copy-caption-btn">
                  {copied ? <><Check className="w-3 h-3" /> kopiert</> : <><Copy className="w-3 h-3" /> kopieren</>}
                </button>
              </div>
              <p className="text-sm text-zinc-200 whitespace-pre-line leading-relaxed">{result.caption}</p>
              {result.hashtags?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-zinc-800 flex flex-wrap gap-1.5">
                  {result.hashtags.map((h, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded font-medium" style={{ background: 'rgba(0,212,255,0.08)', color: '#7DD8F4' }}>
                      #{(h || "").replace(/^#/, "")}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-xl bg-zinc-900/40 border border-zinc-800 p-4">
              <p className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <Hash className="w-4 h-4" style={{ color: '#00D4FF' }} /> Story-Flow
              </p>
              <ol className="space-y-2">
                {(result.slides || []).map((s, i) => (
                  <li key={i} className="flex gap-3 text-xs">
                    <span className="font-bold flex-shrink-0 w-5" style={{ color: '#C8FF4D' }}>{i + 1}.</span>
                    <div>
                      <p className="text-white">{s.headline}</p>
                      {s.subline && <p className="text-zinc-500 mt-0.5">{s.subline}</p>}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ColorRow({ label, desc, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-[11px] text-zinc-400 block">{label}</Label>
      <p className="text-[10px] text-zinc-500 mb-1.5">{desc}</p>
      <div className="flex items-center gap-2">
        <input type="color" value={value} onChange={e => onChange(e.target.value)}
               className="w-9 h-9 rounded border border-zinc-700 bg-zinc-950 cursor-pointer"
               data-testid={`${testid}-picker`} />
        <input type="text" value={value} onChange={e => onChange(e.target.value)}
               className="flex-1 px-2 py-1.5 text-xs font-mono uppercase rounded bg-zinc-950 border border-zinc-800 text-white"
               data-testid={`${testid}-hex`} />
      </div>
    </div>
  );
}
